import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ENGINE_VERSION, SCANNER_VERSION } from '@omnialera/ori-signal';
import { readingFromScan } from '@omnialera/ori-protocol';

import api from './lib/api.js';
import { loadIdentity } from './lib/identity.js';
import { record as store } from './lib/record.js';
import * as outbox from './lib/outbox.js';
import { applyUpdate, promptInstall, subscribePwa } from './lib/pwa.js';
import { useT } from './lib/i18n.jsx';
import { LanguagePicker } from './components/ui.jsx';

import Start from './views/Start.jsx';
import Scan from './views/Scan.jsx';
import Ori from './views/Ori.jsx';
import RecordView from './views/Record.jsx';
import Consent from './views/Consent.jsx';
import Care from './views/Care.jsx';
import Pledge from './views/Pledge.jsx';
import Console from './views/Console.jsx';

const APP_VERSION = '1.0.0';

const NAV = ['start', 'scan', 'ori', 'record', 'consent', 'care', 'pledge'];

export default function App() {
  const t = useT();
  const [state, setState] = useState(store.get());
  const [identity, setIdentity] = useState(null);
  const [identityError, setIdentityError] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [recent, setRecent] = useState([]);
  const [connected, setConnected] = useState(false);
  const [consentBusy, setConsentBusy] = useState(null);
  const [consentError, setConsentError] = useState(null);
  const [donateState, setDonateState] = useState(null);
  const [queued, setQueued] = useState(0);
  const [pwa, setPwa] = useState({ installable: false, installed: false, updateReady: false });

  useEffect(() => subscribePwa(setPwa), []);

  const [view, setView] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    const known = [...NAV, 'console'];
    if (known.includes(hash)) return hash;
    return store.get().readings.length ? 'ori' : 'start';
  });

  useEffect(() => store.subscribe(setState), []);

  useEffect(() => {
    window.location.hash = view;
    const onHash = () => {
      const next = window.location.hash.replace('#', '');
      if (next && next !== view) setView(next);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [view]);

  /**
   * A view change is a navigation, and it has to move the page.
   *
   * Above 1000px the nav sits beside the content and nothing needs to move.
   * Below it the shell stacks, and the nav — eight sections, a language picker,
   * a status legend and a footer — is taller than a phone screen on its own. The
   * view you just chose then opens below the fold, and the app looks like it did
   * nothing at all. That is how the operator console came to look as though it
   * had no way in: the form was there, a screen and a half down.
   *
   * Moving focus to the main region fixes both halves at once — the page goes to
   * the content, and a screen reader is told where it now is, which a hash change
   * on its own never announces.
   */
  const settled = useRef(false);
  useEffect(() => {
    const first = !settled.current;
    settled.current = true;
    // A cold load of the front page is not a navigation; a cold load of a deep
    // link is — someone opening /#console asked for the console, not the nav.
    if (first && view === 'start') return;
    const main = document.getElementById('ori-main');
    if (!main) return;
    main.focus({ preventScroll: true });
    if (window.matchMedia('(max-width: 1000px)').matches) {
      main.scrollIntoView({ block: 'start', behavior: first ? 'auto' : 'smooth' });
    }
  }, [view]);

  /* ── identity and consent sync ─────────────────────────────────────── */

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const id = await loadIdentity();
        if (cancelled) return;
        setIdentity(id);

        if (!api.configured) return;
        await api.registerDevice({
          monkTone: store.get().monkTone ?? undefined,
          appVersion: APP_VERSION,
          engineVersion: ENGINE_VERSION,
        });
        // The server's view of consent is authoritative — a device that was
        // wiped and restored from a key must not believe its stale local flags.
        const remote = await api.getConsent(id.deviceId);
        if (!cancelled) store.syncConsent(remote.scopes);
        const summary = await api.deviceSummary(id.deviceId);
        if (!cancelled) store.applyCredits(summary.credits);
      } catch (err) {
        if (!cancelled && err.name !== 'AbortError') {
          setIdentityError(err.message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── live corpus ───────────────────────────────────────────────────── */

  useEffect(() => {
    if (!api.configured) return undefined;
    const unsubscribe = api.subscribeCorpus(
      (next) => {
        setCoverage(next);
        setConnected(true);
      },
      () => setConnected(false),
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!api.configured) return undefined;
    let cancelled = false;
    const pull = async () => {
      try {
        const { contributions } = await api.recentContributions(30);
        if (!cancelled) setRecent(contributions);
      } catch {
        /* the feed is decoration; failing quietly is correct here */
      }
    };
    pull();
    const timer = setInterval(pull, 20000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [coverage?.readings]);

  /* ── actions ───────────────────────────────────────────────────────── */

  const setTone = useCallback((tone) => {
    store.setTone(tone);
  }, []);

  const setConsent = useCallback(async (scope, granted) => {
    setConsentError(null);
    if (!api.configured) {
      // Offline-only build: keep the switch honest by recording it locally,
      // and never pretend a share happened when there is nowhere to share to.
      store.setConsentLocal(scope, granted);
      return;
    }
    setConsentBusy(scope);
    try {
      await api.setConsent(scope, granted);
      store.setConsentLocal(scope, granted);
    } catch (err) {
      setConsentError(
        err.code === 'unknown_device'
          ? 'This device is not registered with the service yet. Reload and try again.'
          : err.message,
      );
    } finally {
      setConsentBusy(null);
    }
  }, []);

  /* ── the outbox ────────────────────────────────────────────────────── */
  // Readings queued while offline are delivered the moment the device comes
  // back, without the person having to remember to reopen the app and press
  // anything. The clinic that most needs this is the one least likely to.
  useEffect(() => {
    let cancelled = false;

    const attempt = async () => {
      if (cancelled || !api.configured || !store.get().consent.corpus) {
        if (!cancelled) setQueued(await outbox.count());
        return;
      }
      const summary = await outbox.flush(api.contribute.bind(api));
      if (cancelled) return;
      setQueued(summary.remaining);
      if (summary.sent > 0) {
        store.markSharedByKeys(summary.sentKeys);
        setDonateState(
          summary.sent === 1
            ? { kind: 'ok', key: 'shared.flushed.one' }
            : { kind: 'ok', key: 'shared.flushed', vars: { count: summary.sent } },
        );
      }
    };

    attempt();
    const unwatch = outbox.watchConnectivity(attempt);
    return () => {
      cancelled = true;
      unwatch();
    };
  }, []);

  const handleScanResult = useCallback(
    async (result) => {
      // Clear the previous scan's outcome before anything can go wrong. Without
      // this, a reading that failed to queue kept the last reading's green
      // "Added to the corpus" sitting under it.
      setDonateState(null);
      const current = store.get();
      const reading = readingFromScan(result, {
        monkTone: current.monkTone ?? 1,
        observedAt: new Date().toISOString(),
        appVersion: APP_VERSION,
      });
      const local = { ...reading, source: 'this device', shared: false };
      store.addReading(local);

      // Donation status travels as a key, not a sentence, so it reaches the
      // reader in the language they chose rather than the one this file is in.
      if (!current.consent.corpus) {
        setDonateState({ kind: 'info', key: 'shared.kept' });
        return;
      }
      if (!api.configured) {
        setDonateState({ kind: 'info', key: 'shared.keptNoService' });
        return;
      }

      // Durable first, network second. If the tab closes between these two
      // lines the reading is still on its way; if it were the other way round
      // it would simply be gone.
      await outbox.enqueue(reading);
      setDonateState({ kind: 'info', key: 'shared.sending' });

      const summary = await outbox.flush(api.contribute.bind(api));
      setQueued(summary.remaining);

      if (summary.sent > 0) {
        try {
          const s = await api.deviceSummary((await loadIdentity()).deviceId);
          store.applyCredits(s.credits);
        } catch {
          /* the credit total is cosmetic; the reading is what matters */
        }
        store.markSharedByKeys(summary.sentKeys);
        setDonateState(
          summary.sent === 1
            ? { kind: 'ok', key: 'shared.added' }
            : { kind: 'ok', key: 'shared.addedWithQueue', vars: { count: summary.sent - 1 } },
        );
      } else if (summary.dropped > 0) {
        setDonateState({ kind: 'error', key: 'shared.refused' });
      } else {
        setDonateState({
          kind: 'info',
          key: 'shared.queuedOffline',
          vars: { count: summary.remaining },
        });
      }
    },
    [],
  );

  /**
   * The scan view fires this and does not wait for it, so nothing above is
   * allowed to reject into the void. IndexedDB is the realistic failure —
   * private browsing, denied storage, a full quota — and the person needs to be
   * told their reading was not kept rather than left looking at a spinner or,
   * worse, at the previous scan's success message.
   */
  const onScanResult = useCallback(
    (result) => {
      handleScanResult(result).catch((err) => {
        console.error('[ori] the reading could not be recorded', err);
        setDonateState({ kind: 'error', key: 'shared.notKept' });
      });
    },
    [handleScanResult],
  );

  const offline = api.configured && !connected;

  const body = useMemo(() => {
    switch (view) {
      case 'scan':
        return <Scan record={state} onResult={onScanResult} donateState={donateState} />;
      case 'ori':
        return <Ori record={state} onGo={setView} />;
      case 'record':
        return <RecordView record={state} identity={identity} />;
      case 'consent':
        return (
          <Consent
            record={state}
            onConsent={setConsent}
            busyScope={consentBusy}
            error={consentError}
            offline={!api.configured}
          />
        );
      case 'care':
        return <Care record={state} />;
      case 'pledge':
        return <Pledge coverage={coverage} connected={connected} offline={offline} />;
      case 'console':
        return <Console coverage={coverage} recent={recent} />;
      case 'start':
      default:
        return (
          <Start
            record={state}
            onTone={setTone}
            onConsent={setConsent}
            onGo={setView}
            consentBusy={Boolean(consentBusy)}
          />
        );
    }
  }, [
    view,
    state,
    identity,
    coverage,
    recent,
    connected,
    offline,
    consentBusy,
    consentError,
    donateState,
    handleScanResult,
    setConsent,
    setTone,
  ]);

  return (
    <div className="shell">
      {/* First in the DOM so it is the first thing a keyboard reaches. The
          sidebar is nine links deep; nobody should have to tab through it to
          reach a scan they came here to take. */}
      <a className="skip" href="#ori-main">
        {t('app.skip')}
      </a>

      <aside className="side">
        <div className="logo">
          <span className="mark" aria-hidden="true" />
          <span className="wm">
            <span className="co">OmniAlera</span>
            <span className="pr">Ori</span>
          </span>
        </div>

        <nav className="navlist" aria-label="Sections">
          {NAV.map((id, i) => (
            <button
              type="button"
              key={id}
              aria-current={view === id ? 'page' : undefined}
              onClick={() => setView(id)}
            >
              <span className="n">{String(i + 1).padStart(2, '0')}</span>
              {t(`nav.${id}`)}
            </button>
          ))}
          <div className="navsep">For OmniAlera</div>
          <button
            type="button"
            aria-current={view === 'console' ? 'page' : undefined}
            onClick={() => setView('console')}
          >
            <span className="n">08</span>
            Operator console
          </button>
        </nav>

        <LanguagePicker />

        <div className="caps">
          <span className={`cap ${identity ? 'on' : identityError ? 'warn' : ''}`}>
            {identity
              ? t('status.deviceKey')
              : identityError
                ? t('status.noKey')
                : t('status.preparingKey')}
          </span>
          <span className={`cap ${connected ? 'on' : api.configured ? 'warn' : ''}`}>
            {!api.configured
              ? t('status.corpusUnset')
              : connected
                ? t('status.corpusLive')
                : t('status.corpusOffline')}
          </span>
          <span className={`cap ${state.consent.corpus ? 'on' : ''}`}>
            {state.consent.corpus ? t('status.sharing') : t('status.notSharing')}
          </span>
          {queued > 0 ? (
            <span className="cap warn">
              {queued === 1 ? t('app.queued.one') : t('app.queued', { count: queued })}
            </span>
          ) : null}
        </div>

        {pwa.installable ? (
          <button type="button" className="btn ghost sideact" onClick={promptInstall}>
            {t('app.install')}
          </button>
        ) : null}

        <p className="sidefoot">
          {t('app.disclaimer')} Engine {ENGINE_VERSION}, scanner {SCANNER_VERSION}.
        </p>
      </aside>

      <main id="ori-main" tabIndex={-1}>
        {pwa.updateReady ? (
          <div className="notice">
            <b>{t('app.update.title')}</b>
            <p>
              {t('app.update.body')}{' '}
              <button type="button" className="linkbtn" onClick={applyUpdate}>
                {t('app.update.action')}
              </button>
            </p>
          </div>
        ) : null}
        {identityError ? (
          <div className="notice bad">
            <b>This browser cannot hold a device key</b>
            <p>
              {identityError} Ori needs a key it can sign with, because a withdrawal has to be impossible for anyone
              else to forge. You can still take readings; nothing can be shared.
            </p>
          </div>
        ) : null}
        {body}
      </main>
    </div>
  );
}
